/** Итог закрытого месяца и предложение подвинуть лимит.
 *
 * Месячная граница — единственный момент, когда решение о лимите имеет смысл:
 * месяц закончился, результат виден, впереди чистый лист. В остальные дни этот
 * разговор ни к чему не привязан.
 *
 * Тон — факт, а не приговор (ADR-0002). «Уложился» и «перебрал» — это описание
 * месяца, а не оценка человека, поэтому здесь нет ни похвалы, ни «зато».
 * Предложение — предложение: цена решения показана, решает человек.
 */
import { useState } from "react";
import { api, type MonthReview } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { Sheet } from "./Sheet.tsx";
import { tap } from "../lib/telegram.ts";
import { hint } from "./ui.tsx";

export function MonthReviewSheet({ review, monthName, onClose, onApplied }: {
  review: MonthReview;
  monthName: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");
  const over = review.overspent !== "0";
  const cut = review.proposal !== null && Number(review.proposal) < Number(review.limit);

  const apply = () => {
    if (busy) return;
    if (review.proposal === null) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.setAllowance(review.proposal, review.base_currency)
      .then(onApplied)
      .catch(() => setTrouble("Не получилось поставить новую сумму. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={`Итог: ${monthName}`} onClose={onClose}>
      <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
        <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                      letterSpacing: "-0.8px" }}>
          {formatMoney(review.spent, review.base_currency)}
        </div>
        <div className="num" style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)", marginTop: 4 }}>
          из {formatMoney(review.limit, review.base_currency)}
          {" · "}
          {over
            ? `перебрал на ${formatMoney(review.overspent, review.base_currency)}`
            : `осталось ${formatMoney(review.saved, review.base_currency)}`}
        </div>
      </div>

      {review.proposal === null ? (
        <p style={hint}>
          Лимит менять незачем: месяцы идут по-разному, и устойчивого промаха в одну
          сторону нет. Оставляем как есть.
        </p>
      ) : (
        <>
          <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                        padding: "var(--s-4)" }}>
            <div style={{ fontSize: "var(--f-body-s)", lineHeight: 1.6 }}>
              {cut ? "Тратишь меньше лимита" : "Лимит регулярно не сходится"}
              {" "}
              {plural(review.streak, ["месяц", "месяца", "месяцев"])} подряд.
              {" "}
              Можно поставить{" "}
              <span className="num" style={{ fontWeight: 600 }}>
                {formatMoney(review.proposal, review.base_currency)}
              </span>.
            </div>
            <div style={{ ...hint, marginTop: "var(--s-3)" }}>
              {/* Цена решения — обязательная часть предложения: без неё это
                  совет, а совет продукт не даёт (ADR-0002). */}
              {cut
                ? `Накопления вырастут на ${formatMoney(review.savings_delta, review.base_currency)} в месяц.`
                : `Накопления уменьшатся на ${formatMoney(review.savings_delta.replace("-", ""), review.base_currency)} в месяц.`}
            </div>
          </div>

          {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}

          <button type="button" aria-disabled={busy || undefined} onClick={apply}
                  style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                           padding: 14, borderRadius: "var(--r-field)", border: "none",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                           fontWeight: 600, background: "var(--accent)",
                           color: "var(--on-accent)" }}>
            Поставить {formatMoney(review.proposal, review.base_currency)}
          </button>
        </>
      )}

      <button type="button" onClick={() => { tap(); onClose(); }}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-2)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       background: "var(--surface-2)", color: "var(--text)" }}>
        Оставить как есть
      </button>
    </Sheet>
  );
}

